import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NavGroup, NavItem } from '../../shell/nav.config';
import { AuthService } from '../../../auth/data-access/auth.service';
import { toTaxCountry } from '../../../tax-center/models/tax-center.models';
import { ReferralModalService } from '../../../referral/data-access/referral-modal.service';
import { SidebarStateService } from '../../shell/sidebar-state.service';
import { SubscriptionStore } from '../../../subscription/data-access/subscription.store';

@Component({
  selector: 'app-shell-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly referralModal = inject(ReferralModalService);
  private readonly subscriptionStore = inject(SubscriptionStore);
  private readonly sidebarState = inject(SidebarStateService);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) groups: NavGroup[] = [];
  @Input() mobileOpen = false;
  @Output() itemSelected = new EventEmitter<void>();

  /** Desktop icon-rail mode. Shared with the shell so the header and content offsets follow. */
  readonly collapsed = this.sidebarState.collapsed;

  readonly profileMenuOpen = signal(false);
  readonly notificationsOpen = signal(false);
  /** Whether the collapsible "More" group (coming-soon items) is expanded. Collapsed by default. */
  readonly moreExpanded = signal(false);

  toggleCollapsed(): void {
    this.sidebarState.toggle();
    // A rail has no room for the flyout, and leaving it open across the width change strands it
    // beside a sidebar that is no longer the width it was anchored to.
    this.profileMenuOpen.set(false);
  }

  toggleMore(): void {
    this.moreExpanded.update((v) => !v);
  }

  readonly user = computed(() => this.authService.currentUser());

  // Nav groups with gated items removed. Two gates:
  //  • `countries`: shows only for those tax countries (e.g. Harvestable Losses). Country is
  //    normalized via toTaxCountry so an aliased code (GB) resolves to its tax country (UK);
  //    unknown/null → '' → gated items hidden.
  //  • `requiresSubscriptionMode`: hidden while billing/subscription mode is off (e.g. the referral
  //    entry point, since referral rewards depend on paid subscriptions).
  // Empty groups drop.
  readonly visibleGroups = computed<NavGroup[]>(() => {
    const country = toTaxCountry(this.user()?.country) ?? '';
    const subscriptionMode = this.subscriptionStore.subscriptionModeEnabled();
    return this.groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) =>
            (!i.countries || i.countries.includes(country)) &&
            (!i.requiresSubscriptionMode || subscriptionMode),
        ),
      }))
      .filter((g) => g.items.length > 0);
  });

  readonly userInitials = computed(() => {
    const name = this.user()?.name ?? '';
    if (!name) return 'JD';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || name.slice(0, 2).toUpperCase();
  });

  readonly userName = computed(() => this.user()?.name ?? 'Guest');
  readonly userEmail = computed(() => this.user()?.email ?? '—');

  /** Length of the post-signup upgrade window, in days. */
  private static readonly UPGRADE_WINDOW_DAYS = 30;
  /** How often the clock signal is bumped — hourly is ample for a whole-day counter. */
  private static readonly TICK_MS = 60 * 60 * 1000;

  /**
   * Reactive "current time". `Date.now()` on its own is not a signal, so a computed that reads it
   * would never recompute — the countdown would freeze at whatever it was when the sidebar first
   * rendered (this shell stays mounted for the whole session, which can span days).
   */
  private readonly nowTick = signal(Date.now());

  /**
   * Days left in the post-signup upgrade window, counted from the account's creation date.
   * Clamped to 0…30; null when the signup date isn't known (older sessions), so the card can
   * still render without showing a wrong number.
   */
  readonly upgradeDaysLeft = computed<number | null>(() => {
    const createdAt = this.user()?.created_at;
    if (!createdAt) return null;
    const created = new Date(createdAt).getTime();
    if (Number.isNaN(created)) return null;
    const elapsedDays = Math.floor((this.nowTick() - created) / 86_400_000);
    const left = SidebarComponent.UPGRADE_WINDOW_DAYS - elapsedDays;
    return Math.max(0, Math.min(SidebarComponent.UPGRADE_WINDOW_DAYS, left));
  });

  constructor() {
    // Advance the clock signal so the countdown stays correct across a long session. Cleared with
    // the component so the interval can't outlive it.
    const tick = setInterval(
      () => this.nowTick.set(Date.now()),
      SidebarComponent.TICK_MS,
    );
    this.destroyRef.onDestroy(() => clearInterval(tick));
  }

  /**
   * The upgrade nudge is only for users who can actually act on it: billing must be on and they
   * must still be on the free plan. It disappears the moment they subscribe.
   */
  readonly showUpgradeCard = computed(
    () => this.subscriptionStore.subscriptionModeEnabled() && !this.subscriptionStore.isPremium(),
  );

  goToPricing(): void {
    this.navigateFromMenu(['/app/pricing']);
  }

  /**
   * Billing is only a meaningful destination while subscription mode is on — same gate the
   * upgrade card and the `requiresSubscriptionMode` nav items use.
   */
  /** Navigate from the account menu: close the menu, and the mobile drawer with it. */
  private navigateFromMenu(commands: unknown[]): void {
    this.profileMenuOpen.set(false);
    this.itemSelected.emit();
    this.router.navigate(commands as string[]);
  }

  // Backend has no notifications system yet — empty list shows the empty state.
  readonly notifications = signal<Array<{
    id: number;
    title: string;
    description: string;
    time: string;
    type: 'success' | 'warning' | 'info';
    read: boolean;
  }>>([]);
  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.read).length);

  toggleProfileMenu(): void {
    this.profileMenuOpen.update((v) => !v);
    if (this.profileMenuOpen()) this.notificationsOpen.set(false);
  }

  closeProfileMenu(): void {
    this.profileMenuOpen.set(false);
  }

  toggleNotifications(): void {
    this.notificationsOpen.update((v) => !v);
    if (this.notificationsOpen()) this.profileMenuOpen.set(false);
  }

  closeNotifications(): void {
    this.notificationsOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.profileMenuOpen.set(false);
    this.notificationsOpen.set(false);
  }

  private readonly profileArea = viewChild<ElementRef<HTMLElement>>('profileArea');

  /** Close the account menu on any click outside it — including clicks on the page content. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.profileMenuOpen()) return;
    const target = event.target as Node | null;
    if (target && this.profileArea()?.nativeElement.contains(target)) return;
    this.profileMenuOpen.set(false);
  }

  onItemClick(): void {
    // The aside sits at z-50, above the menu's click-away catcher, so a click on a nav item never
    // reaches the backdrop — close the account menu here instead of leaving it hanging open.
    this.profileMenuOpen.set(false);
    this.itemSelected.emit();
  }

  /**
   * Central click handler for a nav item. `comingSoon` items are inert; `modal` items pop their
   * modal instead of navigating (the anchor's routerLink is null for them). Everything else
   * behaves as a normal link and just closes the mobile drawer.
   */
  onItemActivate(item: NavItem, event: Event): void {
    if (item.comingSoon) {
      event.preventDefault();
      return;
    }
    if (item.modal === 'referral') {
      event.preventDefault();
      this.referralModal.open();
      this.onItemClick();
      return;
    }
    this.onItemClick();
  }

  onConnectWallet(): void {
    this.itemSelected.emit();
    this.router.navigate(['/app/integrations'], { queryParams: { connect: 1 } });
  }

  onLogout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.profileMenuOpen.set(false);
        this.router.navigate(['/auth/login']);
      },
      error: (err) => {
        console.error('Logout failed:', err);
        // Ensure menu is closed and redirected even if server call fails
        this.profileMenuOpen.set(false);
        this.router.navigate(['/auth/login']);
      }
    });
  }

  onMarkAllRead(): void {
    this.notifications.update((items) => items.map((n) => ({ ...n, read: true })));
  }
}
